// InviteByLinkTab — the "Invite by link & QR code" tab of AddPeopleDrawer.
// Configure the roles (required) + placement (optional) applied to anyone who
// joins via the link, which profile fields the invitee must fill in, and a
// notify preference. The invite link + QR come from the company invite code
// (fetched by the parent). Controlled by the parent, which owns the draft
// (so its footer can Save/Reset) and fetches/seeds the invite settings.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { Button, Checkbox, Icon, Switch } from '@/components/common';
import { ADMIN_PANEL_URL } from '@/config/config';
import { copyText } from '@/utils/clipboard';
import { useGetRolesSelectionQuery } from '@/api/endpoints/rolesApi';
import { useGetStructureSelectionQuery } from '@/api/endpoints/structureApi';
import { useLevelConfig } from '@/api/endpoints/levelsApi';
import {
  useSendInviteLinkToMobileMutation,
  type InviteLinkSettings,
  type RequiredField,
} from '@/api/endpoints/companyApi';
import type { OrgUnit } from '@/mocks/structure';
import { useAppSelector } from '@/store/hooks';

export interface InviteDraft {
  roleIds: string[];
  nodeIds: string[];
  notify: boolean;
  requestedInfo: string[];
}

export const emptyInviteDraft: InviteDraft = {
  roleIds: [],
  nodeIds: [],
  notify: true,
  requestedInfo: ['firstName', 'lastName', 'phone', 'email', 'department'],
};

// Fields the invitee MUST fill (required: true); every other asked field is
// optional (required: false). These UI keys are always-asked + locked-on.
export const LOCKED_KEYS = ['firstName', 'lastName', 'phone'];

// UI field key ⇄ backend `fieldName`. Only `phone` differs; the rest are 1:1.
const FIELD_NAME_BY_KEY: Record<string, string> = { phone: 'mobileNumber' };
const KEY_BY_FIELD_NAME: Record<string, string> = { mobileNumber: 'phone' };
const toFieldName = (key: string) => FIELD_NAME_BY_KEY[key] ?? key;
const toKey = (fieldName: string) => KEY_BY_FIELD_NAME[fieldName] ?? fieldName;

/** Draft's requested fields → API `requiredFields` (locked ⇒ required: true). */
export const buildRequiredFields = (requestedInfo: string[]): RequiredField[] =>
  requestedInfo.map((key) => ({
    fieldName: toFieldName(key),
    required: LOCKED_KEYS.includes(key),
  }));

/** Server invite settings → the local draft (reverse of the payload build). */
export const settingsToDraft = (s: InviteLinkSettings): InviteDraft => ({
  roleIds: s.roles,
  nodeIds: s.companyNodes,
  notify: s.notifyMeOnJoinCompany,
  requestedInfo: s.requiredFields.map((f) => toKey(f.fieldName)),
});

interface InviteByLinkTabProps {
  draft: InviteDraft;
  patch: (changes: Partial<InviteDraft>) => void;
  submitted: boolean;
  /** Company invite code — builds the invite link + QR value. */
  inviteCode?: string;
  /** Link/QR are active (un-faded) only once invite settings exist on the server. */
  linkActive?: boolean;
}

/** Flatten the org tree to rows carrying their depth for indentation. */
const flattenTree = (units: OrgUnit[], depth = 0): { unit: OrgUnit; depth: number }[] =>
  units.flatMap((unit) => [
    { unit, depth },
    ...(unit.children ? flattenTree(unit.children, depth + 1) : []),
  ]);

// Profile fields the invitee can be asked for. First/last/phone are always asked.
const REQUESTED_FIELDS: { key: string; labelKey: string; icon: string; locked?: boolean }[] = [
  { key: 'firstName', labelKey: 'add_person_firstname', icon: 'user-round', locked: true },
  { key: 'lastName', labelKey: 'add_person_lastname', icon: 'user-round', locked: true },
  { key: 'phone', labelKey: 'add_person_phone', icon: 'phone', locked: true },
  { key: 'email', labelKey: 'guest_label_email', icon: 'mail' },
  { key: 'department', labelKey: 'add_person_department', icon: 'building-2' },
  { key: 'employmentType', labelKey: 'add_person_emptype', icon: 'briefcase' },
  { key: 'personalIdNumber', labelKey: 'add_person_idnumber', icon: 'id-card' },
  { key: 'personalIdExpiry', labelKey: 'add_person_idexpiry', icon: 'calendar' },
  { key: 'birthDate', labelKey: 'add_person_birthdate', icon: 'cake' },
  { key: 'nationality', labelKey: 'add_person_nationality', icon: 'flag' },
];

const InviteByLinkTab = ({
  draft,
  patch,
  submitted,
  inviteCode,
  linkActive,
}: InviteByLinkTabProps) => {
  const { t } = useTranslation();
  const { data: roles = [] } = useGetRolesSelectionQuery();
  const { data: roots = [] } = useGetStructureSelectionQuery();
  const levelConfig = useLevelConfig();
  const [sendToMobile, { isLoading: sending }] = useSendInviteLinkToMobileMutation();
  const user = useAppSelector((state) => state.auth.user);

  const treeRows = useMemo(() => flattenTree(roots), [roots]);
  const rolesMissing = draft.roleIds.length === 0;

  // Link/QR go live only once the server has invite settings; until then the
  // preview stays faded and shows the bare base URL.
  //
  // The live link is the company invite code under this deployment's /invite
  // route — ADMIN_PANEL_URL, not a hardcoded host, so a code copied on local or
  // develop can't enrol someone against production. The faded state needs no
  // translation key: it is a URL, which is data, not copy.
  const active = Boolean(linkActive);
  const joinLink = inviteCode ? `${ADMIN_PANEL_URL}/invite/${inviteCode}` : '';
  const linkValue = active && joinLink ? joinLink : ADMIN_PANEL_URL;

  // Copies the real link, not `linkValue` — the button is disabled unless a code
  // exists, so the bare-base placeholder can never reach the clipboard.
  const copyLink = async () => {
    try {
      await copyText(joinLink);
      toast.success(t('invite_link_copied'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // Bodyless call — the server knows who is asking and which link to send, so
  // there is nothing to pass and nothing local to update afterwards. The toast
  // names the signed-in user's own phone (the number the server sends to),
  // LTR-isolated so "+966 5…" keeps its order inside the Arabic sentence.
  const sendLink = async () => {
    try {
      await sendToMobile().unwrap();
      const phone = `${user?.countryCode ?? ''} ${user?.mobileNumber ?? ''}`.trim();
      toast.success(t('invite_link_sent_mobile', { phone: `\u2066${phone}\u2069` }));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const toggle = (key: 'roleIds' | 'nodeIds', id: string) =>
    patch({
      [key]: draft[key].includes(id) ? draft[key].filter((x) => x !== id) : [...draft[key], id],
    });
  const toggleRequested = (key: string, locked?: boolean) => {
    if (locked) return;
    patch({
      requestedInfo: draft.requestedInfo.includes(key)
        ? draft.requestedInfo.filter((x) => x !== key)
        : [...draft.requestedInfo, key],
    });
  };

  return (
    <div className='add-person'>
      {/* ---- INVITE LINK (faded until settings exist on the server) ---- */}
      <section className='add-person__card'>
        <p className='add-person__card-head'>
          <Icon name='link' size={16} />
          {t('invite_link_section')}
          {!active && (
            <span className='add-person__card-status'>{t('invite_link_status_none')}</span>
          )}
        </p>
        <div className='add-person__card-body'>
          {!active && (
            <p className='invite-tab__banner'>
              <Icon name='info' size={16} />
              <span>{t('invite_link_banner')}</span>
            </p>
          )}
          {/* Faded + inert until invite settings exist; active once they do. */}
          <div className={`invite-tab__preview${active ? ' is-active' : ''}`}>
            <div className='invite-tab__link-row'>
              <span className='invite-tab__link'>{linkValue}</span>
              {/* `disabled` as well as the wrapper's pointer-events: none — that
                  alone leaves the button in the tab order, so a keyboard user
                  could copy the placeholder out of the faded state. */}
              <Button
                variant='secondary'
                iconStart='copy'
                disabled={!active || !joinLink}
                onClick={copyLink}
              >
                {t('invite_link_copy')}
              </Button>
              <Button
                variant='secondary'
                iconStart='smartphone'
                disabled={!active || !joinLink || sending}
                loading={sending}
                onClick={sendLink}
              >
                {t('invite_link_send_mobile')}
              </Button>
            </div>
            <div className='invite-tab__qr-block'>
              <span className='invite-tab__qr'>
                <QRCodeSVG value={linkValue} size={140} bgColor='#ffffff' fgColor='#0c1116' />
              </span>
              <div className='invite-tab__qr-text'>
                <span className='invite-tab__qr-title'>{t('invite_qr_title')}</span>
                <p className='invite-tab__qr-hint'>{t('invite_qr_hint')}</p>
              </div>
            </div>
          </div>
          <Checkbox
            label={t('invite_notify_label')}
            checked={draft.notify}
            onChange={(e) => patch({ notify: e.target.checked })}
          />
        </div>
      </section>

      {/* ---- ROLES (required) ---- */}
      <section className='add-person__card'>
        <p className='add-person__card-head'>
          <Icon name='shield-check' size={16} />
          {t('add_person_roles_label')}
          <span className='add-person__card-status'>{t('invite_required')}</span>
        </p>
        <div className='add-person__card-body'>
          <p className='invite-tab__desc'>{t('invite_roles_hint')}</p>
          <div className='add-person__pills'>
            {roles.map((role) => {
              const selected = draft.roleIds.includes(role.id);
              return (
                <button
                  key={role.id}
                  type='button'
                  className={`people-role-pill${selected ? ' people-role-pill--selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => toggle('roleIds', role.id)}
                >
                  <Icon name={selected ? 'check' : 'shield-check'} size={15} />
                  {role.title}
                </button>
              );
            })}
          </div>
          {submitted && rolesMissing && (
            <p className='add-person__error'>
              <Icon name='circle-alert' size={14} />
              {t('add_person_err_roles')}
            </p>
          )}
        </div>
      </section>

      {/* ---- COMPANY STRUCTURE (optional) ---- */}
      <section className='add-person__card'>
        <p className='add-person__card-head'>
          <Icon name='network' size={16} />
          {t('add_person_structure_label')}
          <span className='add-person__card-status'>{t('invite_optional')}</span>
        </p>
        <div className='add-person__card-body'>
          <p className='invite-tab__desc'>{t('invite_structure_hint')}</p>
          <div className='add-person__tree'>
            {treeRows.map(({ unit, depth }) => {
              const indent = {
                paddingInlineStart: `calc(var(--space-3) + ${depth} * var(--space-5))`,
              };
              if (unit.level === 0) {
                return (
                  <div
                    key={unit.id}
                    className='add-person__tree-row add-person__tree-row--static'
                    style={indent}
                  >
                    <Icon
                      name={levelConfig(unit.level).icon}
                      size={16}
                      className='add-person__tree-icon'
                    />
                    <span className='add-person__tree-name'>{unit.name}</span>
                  </div>
                );
              }
              const selected = draft.nodeIds.includes(unit.id);
              return (
                <button
                  key={unit.id}
                  type='button'
                  className={`add-person__tree-row${selected ? ' is-selected' : ''}`}
                  aria-pressed={selected}
                  style={indent}
                  onClick={() => toggle('nodeIds', unit.id)}
                >
                  <Icon
                    name={levelConfig(unit.level).icon}
                    size={16}
                    className='add-person__tree-icon'
                  />
                  <span className='add-person__tree-name'>{unit.name}</span>
                  {selected && <Icon name='check' size={16} className='add-person__tree-check' />}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---- REQUESTED INFO ---- */}
      <section className='add-person__card'>
        <p className='add-person__card-head'>
          <Icon name='list-checks' size={16} />
          {t('invite_requested_section')}
          <span className='add-person__card-status'>
            {t('invite_requested_count', {
              selected: draft.requestedInfo.length,
              total: REQUESTED_FIELDS.length,
            })}
          </span>
        </p>
        <div className='add-person__card-body'>
          <p className='invite-tab__desc'>{t('invite_requested_hint')}</p>
          <div className='invite-tab__req-list'>
            {REQUESTED_FIELDS.map((f) => (
              <div key={f.key} className='invite-tab__req-row'>
                <span className='invite-tab__req-lead'>
                  <Icon name={f.icon} size={18} className='invite-tab__req-icon' />
                  <span className='invite-tab__req-name'>{t(f.labelKey)}</span>
                  {f.locked && (
                    <span className='invite-tab__req-lock'>
                      <Icon name='lock' size={13} />
                      {t('invite_always_asked')}
                    </span>
                  )}
                </span>
                <Switch
                  checked={draft.requestedInfo.includes(f.key)}
                  disabled={f.locked}
                  onChange={() => toggleRequested(f.key, f.locked)}
                  aria-label={t(f.labelKey)}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InviteByLinkTab;
